import { useEffect, useState } from 'react';
import { useAuthStore } from '@/entities/session/model/authStore';
import { api } from '@/shared/api/axios';
import { env } from '@/shared/config/env';

export function AppBootGate({ children }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    async function boot() {
      // DEV is a compile-time constant, so in prod this folds to false and every
      // branch below (incl. the auto-login) is tree-shaken out — never reaches a real backend.
      const devMocks = import.meta.env.DEV && env.ENABLE_MSW;

      if (devMocks) {
        const { worker } = await import('@/shared/mocks/browser');
        await worker.start({
          onUnhandledRequest: 'bypass',
          serviceWorker: { url: '/mockServiceWorker.js' },
        });
      } else if (import.meta.env.DEV) {
        // Unregister any previously cached service worker so MSW doesn't intercept real API calls.
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
      }

      // No session yet → open as Ripik admin (skips the login wall). An explicit
      // login takes over; an existing token is never overridden.
      if (devMocks && !useAuthStore.getState().token) {
        useAuthStore.getState().setAuth({
          token: 'mock-jwt-usr-001',
          user: { id: 'usr-001', email: 'admin@ripik.ai', name: 'Ripik Admin' },
          assignments: [],
        });
      }

      // Refresh assignments from /auth/me. In mock mode the service worker may
      // not be controlling the page yet on a brand-new origin (e.g. first load
      // of a static demo deploy), so the first call can miss the mock — retry a
      // few times, and never evict the mock session on failure.
      const { token, setAuth, clearAuth } = useAuthStore.getState();
      if (token) {
        let attempts = devMocks ? 6 : 1;
        for (;;) {
          try {
            const { data } = await api.get('/auth/me');
            setAuth({ token, user: { id: data.id, email: data.email, name: data.name }, assignments: data.assignments });
            break;
          } catch {
            attempts -= 1;
            if (attempts <= 0) {
              if (!devMocks) clearAuth(); // demo session must persist; real session is cleared
              break;
            }
            await new Promise((r) => setTimeout(r, 150));
          }
        }
      }

      setReady(true);
    }

    boot();
  }, []);

  if (!ready) return null;

  return children;
}
